import { z } from 'zod';

/**
 * Nature d'un brouillon canvas.
 * - generated : produit par l'agent (plan/acte) dans un canvas neuf
 * - imported  : issu de l'édition d'une pièce Word existante (original intact, D2)
 * - blank     : document vierge démarré à la main
 */
export const CanvasDraftKindSchema = z.enum(['generated', 'imported', 'blank']);
export type CanvasDraftKind = z.infer<typeof CanvasDraftKindSchema>;

/**
 * Brouillon canvas persisté : 1 row `canvas_drafts` + 1 objet `.docx` S3.
 * `caseId`/`conversationId` nullable : un canvas peut flotter sans dossier
 * ni conversation (D4/D5). Les timestamps sont des strings ISO (le service
 * backend convertit les Date pg via toISOString — cf. mapRow).
 */
/**
 * Le nom qu'un brouillon porte tant que personne ne l'a nommé.
 *
 * ⚠️ **Ce n'est pas un titre, c'est une absence de titre.** Il était écrit en
 * dur à cinq endroits, et l'un d'eux l'a traité comme un vrai nom : le titre
 * effectif d'un brouillon rouvert valait donc « Document sans titre », ce qui
 * éteignait le nommage automatique dès le premier rechargement de page et
 * faisait croire à l'agent qu'il regardait un document nommé (corrigé le
 * 2026-09-22).
 *
 * Toute surface qui décide « ce document a-t-il un nom ? » doit comparer à
 * cette constante, jamais à une copie de la chaîne.
 */
export const CANVAS_DRAFT_DEFAULT_TITLE = 'Document sans titre';

export const CanvasDraftSchema = z.object({
  id: z.string().uuid(),
  cabinetId: z.string().uuid(),
  userId: z.string().uuid(),
  caseId: z.string().uuid().nullable(),
  conversationId: z.string().uuid().nullable(),
  title: z.string(),
  kind: CanvasDraftKindSchema,
  sourceDocumentId: z.string().uuid().nullable(),
  s3Key: z.string(),
  fileSize: z.number().int().nonnegative().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Exact accepted server attempt; absent for drafts created before integrity gating. */
  integrityAttemptId: z.string().uuid().optional(),
});
export type CanvasDraft = z.infer<typeof CanvasDraftSchema>;

/**
 * Versions of the browser runtime that exported one exact DOCX candidate.
 *
 * This is deliberately limited to build/runtime identifiers: it enables a
 * support reproduction without retaining document text, browser state, or a
 * potentially identifying user-agent string.
 */
const CanvasRuntimeVersionSchema = z.union([
  // A deployed browser reports either the immutable commit SHA or the package
  // semver that built it. Keeping this vocabulary closed prevents an arbitrary
  // multipart field from turning the forensic register into a text sink.
  z.string().regex(/^[0-9a-f]{7,64}$/i, 'runtime version must be a commit SHA'),
  z.string().regex(/^v?\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?(?:\+[0-9A-Za-z.-]+)?$/, 'runtime version must be semver'),
  // A short rolling-deploy bridge for tabs loaded before #1668. These values
  // deliberately describe missing provenance instead of inventing a version.
  z.literal('legacy-client'),
  z.literal('not-reported'),
  z.literal('server-revalidation'),
  z.literal('not-applicable'),
]);

export const CanvasDraftRuntimeFingerprintSchema = z.object({
  applicationVersion: CanvasRuntimeVersionSchema,
  superdocReactVersion: CanvasRuntimeVersionSchema,
}).strict();
export type CanvasDraftRuntimeFingerprint = z.infer<typeof CanvasDraftRuntimeFingerprintSchema>;

/**
 * Métadonnées texte accompagnant le fichier dans le multipart PUT /:id.
 * Le fichier .docx voyage à part (champ `file`). NB : l'id du brouillon est
 * dans le path (`:id`) et validé uuid côté route — il ne passe PAS par ici.
 */
export const CanvasDraftUpsertMetaSchema = z.object({
  /** Stable across retries of the same captured bytes, never reused for another edit. */
  attemptId: z.string().uuid().optional(),
  conversationId: z.string().uuid().nullable().default(null),
  caseId: z.string().uuid().nullable().default(null),
  title: z.string().min(1).default(CANVAS_DRAFT_DEFAULT_TITLE),
  kind: CanvasDraftKindSchema.default('blank'),
  sourceDocumentId: z.string().uuid().nullable().default(null),
  /** Runtime that produced the attached candidate; mandatory for support traceability. */
  runtimeFingerprint: CanvasDraftRuntimeFingerprintSchema,
});


/** Product budget for one Canvas DOCX upload/download, shared with the checkpoint deadline. */
export const CANVAS_DRAFT_TRANSFER_TIMEOUT_MS = 30_000;

/**
 * How many drafts one conversation may hold.
 *
 * A growth guard, not a correctness invariant: nothing breaks at 11, the table
 * just grows without an owner ever cleaning it. Until the browser gained a way
 * to list and pick a draft, the server destroyed the siblings after every
 * publication, and that destruction WAS the only bound on the table.
 *
 * Ten because it is also what a reader shows at once (`list_canvas_drafts`):
 * capping writes above the read limit would let a lawyer create documents that
 * the agent cannot see — the worst of both. A refusal, never an eviction: a
 * draft is the lawyer's work, and evicting the oldest would destroy an act in
 * silence.
 */
export const CANVAS_DRAFTS_PER_CONVERSATION_LIMIT = 10;
